import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { Skeleton, useMediaQuery, useTheme } from "@mui/material"; // Add useMediaQuery and useTheme from Material UI
import { headingFonts } from "../../../Styles/Styles";

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MonthlyAds = () => {
  const [chartData, setChartData] = useState(null);
  const [maxYValue, setMaxYValue] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Check if the screen size is small

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/charts/monthly-ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`, // Ensure token is added
            },
          }
        );

        if (response.status === 401) {
          setError("Unauthorized. Please log in.");
          navigate("/get-started");
          return;
        }

        if (response.status === 403) {
          setError(
            "Access denied. You don't have permission to view this data."
          );
          return;
        }

        const data = await response.json();

        const labels = data.map((stat) => stat.monthYear);
        const postedCounts = data.map((stat) => stat.postedCount);
        const deletedCounts = data.map((stat) => stat.deletedCount);
        const soldCounts = data.map((stat) => stat.soldCount);

        const totalMax = Math.max(
          ...postedCounts.map(
            (count, index) => count + deletedCounts[index] + soldCounts[index]
          )
        );
        setMaxYValue(totalMax * 2);

        setChartData({
          labels,
          datasets: [
            {
              label: "Ads Posted",
              data: postedCounts,
              backgroundColor: "rgba(75, 192, 192, 0.6)",
              borderColor: "rgba(75, 192, 192, 1)",
              borderWidth: 1,
              fontFamily: "ClashGrotesk-Medium",
              letterSpacing: 1,
            },
            {
              label: "Ads Deleted",
              data: deletedCounts,
              backgroundColor: "rgba(255, 99, 132, 0.6)",
              borderColor: "rgba(255, 99, 132, 1)",
              borderWidth: 1,
            },
            {
              label: "Ads Sold",
              data: soldCounts,
              backgroundColor: "rgba(54, 162, 235, 0.6)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
            },
          ],
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
        setError("An error occurred while fetching data.");
      }
    };

    fetchStats();
  }, [navigate]);

  return (
    <div style={{ width: "100%", padding: isMobile ? "10px" : "20px" }}>
      <h4
        style={{
          ...headingFonts,
          textAlign: "center",
        }}
      >
        Monthly Ad Statistics
      </h4>
      {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}

      {!chartData && !error ? (
        <Skeleton variant="rectangular" width="100%" height={400} />
      ) : (
        chartData && (
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: isMobile ? "bottom" : "top",
                  labels: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
                title: {
                  display: true,
                  text: "Number of Ads Posted, Deleted, and Sold Per Month-Year",
                  font: {
                    family: "ClashGrotesk-Regular",
                  },
                  letterSpacing: 1,
                },
              },
              scales: {
                x: {
                  ticks: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
                y: {
                  beginAtZero: true,
                  max: maxYValue,
                  ticks: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
              },
            }}
            style={{
              ...headingFonts,
              maxHeight: isMobile ? "300px" : "500px",
            }}
          />
        )
      )}
    </div>
  );
};

export default MonthlyAds;
